import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, FilePlus2, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../lib/api';
import { publicImageUrl } from '../../lib/media';

const PAGE_ROOTS = new Set([
  'dashboard', 'pos', 'inventory', 'purchases', 'suppliers', 'near-expiry', 'expired-products', 'reorder',
  'stock-adjustments', 'stock-take', 'sales-history', 'controlled-registry', 'recall-report', 'prescriptions',
  'reports', 'daily-summary', 'customers', 'settings', 'help',
]);

export const isAppPath = (href = '') => href.startsWith('/') && !href.startsWith('//') && PAGE_ROOTS.has(href.slice(1).split(/[/?#]/)[0]);

export const chatUrlTransform = (url = '') => {
  if (/^(product-image|chart:(stock|sales)):[\w-]+$/.test(url)) return url;
  if (isAppPath(url) || /^(https?:|mailto:|tel:)/i.test(url)) return url;
  return '';
};

const cache = new Map();
const loadMedia = (id) => {
  if (!cache.has(id)) cache.set(id, api.get(`/claude/media/product/${id}`).then(r => r.data).catch(() => { cache.delete(id); return null; }));
  return cache.get(id);
};

export const ChatLink = ({ href, children, internalLinks }) => {
  const navigate = useNavigate();
  if (!isAppPath(href)) {
    return href ? <a href={href} target="_blank" rel="noreferrer" className="underline text-primary">{children}</a> : <span>{children}</span>;
  }
  if (!internalLinks) return <span className="font-medium">{children}</span>;
  const isForm = href.includes('new=1');
  const Icon = isForm ? FilePlus2 : ArrowUpRight;
  return (
    <button type="button" onClick={() => navigate(href)} data-testid={`chat-link-${href.slice(1).split(/[/?]/)[0]}`}
      className="inline-flex items-center gap-1 my-0.5 mr-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/20">
      <Icon className="h-3 w-3" />{children}
    </button>
  );
};

const useProductMedia = (id) => {
  const [state, setState] = useState({ loading: true, data: null });
  useEffect(() => {
    let live = true;
    loadMedia(id).then(data => live && setState({ loading: false, data }));
    return () => { live = false; };
  }, [id]);
  return state;
};

const MediaFrame = ({ children, testId }) => (
  <span data-testid={testId} className="block my-2 rounded-lg border bg-background/60 p-2">{children}</span>
);

const ProductPhoto = ({ id, alt }) => {
  const { loading, data } = useProductMedia(id);
  if (loading) return <Loader2 className="h-4 w-4 animate-spin my-2" />;
  if (!data?.image_url) return null;
  return (
    <MediaFrame testId="chat-product-photo">
      <img src={publicImageUrl(data.image_url)} alt={alt || data.name} className="h-28 w-full rounded object-contain bg-white" loading="lazy" />
      <span className="block text-[11px] text-muted-foreground mt-1">{data.name}</span>
    </MediaFrame>
  );
};

const MiniChart = ({ id, kind, alt }) => {
  const { loading, data } = useProductMedia(id);
  if (loading) return <Loader2 className="h-4 w-4 animate-spin my-2" />;
  if (!data) return null;
  const rows = kind === 'sales' ? data.sales_14d : data.batches;
  const xKey = kind === 'sales' ? 'date' : 'batch';
  const yKey = kind === 'sales' ? 'units' : 'quantity';
  return (
    <MediaFrame testId={`chat-chart-${kind}`}>
      <span className="block text-[11px] font-medium mb-1">{alt || (kind === 'sales' ? 'Sales, last 14 days' : 'Stock by batch')} · {data.name}</span>
      {rows.some((r) => r[yKey] > 0) ? (
        <span className="block h-28 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
              <XAxis dataKey={xKey} tick={{ fontSize: 9 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 11 }} />
              <Bar dataKey={yKey} fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </span>
      ) : <span className="block text-[11px] text-muted-foreground" data-testid="chat-chart-empty">{kind === 'sales' ? 'No sales in the last 14 days.' : 'No stock on hand.'}</span>}
    </MediaFrame>
  );
};

export const ChatImage = ({ src = '', alt }) => {
  const [kind, a, b] = src.split(':');
  if (kind === 'product-image') return <ProductPhoto id={a} alt={alt} />;
  if (kind === 'chart') return <MiniChart kind={a} id={b} alt={alt} />;
  return null;
};
